import { ChangeEvent, FormEvent, useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, projectScope, orgApi, type Repo, type IndexJob, type Project } from "../api/client";
import { getApiKey, getCognitoToken, getProjectId } from "../lib/auth";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import Fade from "@mui/material/Fade";
import Grow from "@mui/material/Grow";
import LinearProgress from "@mui/material/LinearProgress";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CustomButton from "../components/CustomButton";

type IndexMode = "git" | "zip";

// A row is either project-scoped (manual API-key flow, no project_id of its
// own — falls back to the globally stored one) or org-wide (Cognito flow,
// carries its own project_id since rows span multiple projects).
type RepoRow = Repo & { project_id?: string };

export function Projects() {
  // Cognito-only sessions (no ci_ API key) see every repo across the org;
  // the manual "Project sign in" flow is unaffected and stays project-scoped.
  const isCognitoOnly = !getApiKey() && !!getCognitoToken();

  const [repos, setRepos] = useState<RepoRow[]>([]);
  const [jobs, setJobs] = useState<Record<string, IndexJob[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<IndexMode>("git");

  // Working Project — explicit shared context for both import methods below.
  // Cognito sessions may freely pick any project in their org (the backend
  // resolves access per-request from the org, not a bound key — see
  // _resolve_project_for_org). A manual API-key session is hard-bound
  // server-side to exactly one project (require_project/check_scope reject
  // any other project_id with a 403), so its selector is shown for context
  // but locked to that one project — the manual sign-in flow itself is
  // otherwise untouched.
  const [myProjects, setMyProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [workingProjectId, setWorkingProjectId] = useState("");

  useEffect(() => {
    orgApi.listMyProjects()
      .then((list) => {
        if (isCognitoOnly) {
          setMyProjects(list);
          setWorkingProjectId((prev) => prev || list[0]?.id || "");
          return;
        }
        const own = getProjectId();
        const mine = list.filter((p) => p.id === own);
        setMyProjects(mine.length ? mine : own ? [{ id: own, name: "Current project", description: null, created_at: "" }] : []);
        setWorkingProjectId(own || "");
      })
      .catch(() => {
        if (isCognitoOnly) return;
        const own = getProjectId();
        if (own) {
          setMyProjects([{ id: own, name: "Current project", description: null, created_at: "" }]);
          setWorkingProjectId(own);
        }
      })
      .finally(() => setProjectsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Git form
  const [repoUrl, setRepoUrl] = useState("");
  const [repoUrlError, setRepoUrlError] = useState<string | null>(null);
  const [ref, setRef] = useState("");
  const [gitBusy, setGitBusy] = useState(false);

  const GIT_URL_REGEX = /^(https?:\/\/[\w.-]+(:\d+)?(\/[\w._~:@!$&'()*+,;=-]+)+(\.git)?\/?|git@[\w.-]+:[\w./_-]+(\.git)?)$/;
  const isRepoUrlValid = GIT_URL_REGEX.test(repoUrl);

  // ZIP upload form
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [zipName, setZipName] = useState("");
  const [zipBusy, setZipBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list: RepoRow[] = isCognitoOnly
        ? await orgApi.listMyRepos()
        : await api.get<Repo[]>(projectScope("/repos"));
      setRepos(list);
      const map: Record<string, IndexJob[]> = {};
      for (const r of list) {
        try {
          map[r.id] = await api.get<IndexJob[]>(projectScope(`/repos/${r.id}/jobs`, r.project_id));
        } catch {
          map[r.id] = [];
        }
      }
      setJobs(map);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function onIndex(e: FormEvent) {
    e.preventDefault();
    if (!GIT_URL_REGEX.test(repoUrl)) {
      setRepoUrlError("Please enter a valid Git URL (e.g. https://github.com/org/repo or git@github.com:org/repo.git)");
      return;
    }
    setGitBusy(true);
    setError(null);
    try {
      await api.post<IndexJob>("/api/v1/repos", { repo_url: repoUrl, ref, project_id: workingProjectId });
      setRepoUrl("");
      setRef("");
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setGitBusy(false);
    }
  }

  function onFileChange(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setZipFile(f);
    if (f && !zipName) setZipName(f.name.replace(/\.zip$/i, ""));
  }

  async function onUpload(e: FormEvent) {
    e.preventDefault();
    if (!zipFile) return;
    setZipBusy(true);
    setError(null);
    setUploadProgress("Uploading…");
    try {
      const form = new FormData();
      form.append("file", zipFile);
      form.append("project_id", workingProjectId);
      if (zipName.trim()) form.append("display_name", zipName.trim());
      await api.upload<IndexJob>("/api/v1/repos/upload", form);
      setZipFile(null);
      setZipName("");
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await load();
    } catch (e) {
      setError((e as Error).message);
      setUploadProgress(null);
    } finally {
      setZipBusy(false);
    }
  }

  return (
    <Box className="orgs-page">
      {/* Page header */}
      <Fade in timeout={400}>
        <Box className="orgs-header">
          <Box>
            <Typography variant="h5" className="orgs-page-title">
              Repositories
            </Typography>
            <Typography variant="body2" className="login-subtitle" sx={{ mb: 0 }}>
              Set the working project to establish the current context. All repositories added from GitHub or ZIP
              uploads will be associated with the selected project.
            </Typography>
          </Box>
        </Box>
      </Fade>

      {/* Working Project — shared context for both import methods below */}
      <Fade in timeout={450}>
        <Card className="orgs-create-card" sx={{ mb: 3 }}>
          <CardContent sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 260 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "var(--brand-black)", mb: 1 }}>
                Working Project *
              </Typography>
              <FormControl size="small" fullWidth disabled={!isCognitoOnly || projectsLoading}>
                <Select
                  value={workingProjectId}
                  onChange={(e: SelectChangeEvent) => setWorkingProjectId(e.target.value)}
                  displayEmpty
                  aria-label="Working project"
                  className="login-input"
                >
                  {!workingProjectId && (
                    <MenuItem value="" disabled>
                      Select Project
                    </MenuItem>
                  )}
                  {myProjects.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!isCognitoOnly && (
                <Typography variant="caption" sx={{ color: "var(--brand-muted)", mt: 0.5, display: "block" }}>
                  Fixed to the project bound to your signed-in API key.
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
                background: "rgba(203, 46, 186, 0.06)",
                border: "1px solid rgba(203, 46, 186, 0.2)",
                borderRadius: "var(--radius-sm)",
                p: 1.5,
                flex: 1,
                minWidth: 260,
              }}
            >
              <InfoOutlinedIcon fontSize="small" sx={{ color: "var(--brand-pink)", mt: "2px" }} />
              <Typography variant="body2" sx={{ color: "var(--brand-dark)" }}>
                All repositories you add will be associated with this project.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Fade>

      {/* Index card with tabs */}
      <Grow in timeout={500}>
        <Card className="orgs-create-card">
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            {/* Tab bar */}
            <Box className="projects-tabs">
              <button
                className={`projects-tab ${mode === "git" ? "active" : ""}`}
                onClick={() => { setMode("git"); setError(null); }}
              >
                GitHub / Git URL
              </button>
              <button
                className={`projects-tab ${mode === "zip" ? "active" : ""}`}
                onClick={() => { setMode("zip"); setError(null); }}
              >
                Upload ZIP
              </button>
            </Box>

            <Box sx={{ p: 2.5 }}>
              {mode === "git" && (
                <Fade in timeout={300}>
                  <form onSubmit={onIndex}>
                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                      <TextField
                        placeholder="https://github.com/org/repo"
                        value={repoUrl}
                        onChange={(e) => {
                          setRepoUrl(e.target.value);
                          if (repoUrlError) setRepoUrlError(null);
                        }}
                        onBlur={() => {
                          if (repoUrl && !GIT_URL_REGEX.test(repoUrl)) {
                            setRepoUrlError("Please enter a valid Git URL");
                          }
                        }}
                        error={!!repoUrlError}
                        helperText={repoUrlError}
                        size="small"
                        fullWidth
                        className="login-input"
                        sx={{ flex: 3 }}
                      />
                      <TextField
                        placeholder="branch"
                        value={ref}
                        onChange={(e) => setRef(e.target.value)}
                        size="small"
                        className="login-input"
                        sx={{ flex: 1, minWidth: 120 }}
                      />
                      <CustomButton
                        loading={gitBusy}
                        disabled={!isRepoUrlValid || !ref || !!repoUrlError || !workingProjectId}
                        buttonText="Add"
                        buttonTextLoading="Queuing…"
                        handleSubmitClick={() => {}}
                      />
                    </Box>
                  </form>
                </Fade>
              )}

              {mode === "zip" && (
                <Fade in timeout={300}>
                  <form onSubmit={onUpload}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <TextField
                          placeholder="Display name (optional)"
                          value={zipName}
                          onChange={(e) => setZipName(e.target.value)}
                          size="small"
                          fullWidth
                          className="login-input"
                          sx={{ flex: 1 }}
                        />
                        <CustomButton
                          loading={zipBusy}
                          disabled={!zipFile || !workingProjectId}
                          buttonText="Upload & Add"
                          buttonTextLoading={uploadProgress ?? "Uploading…"}
                          handleSubmitClick={() => {}}
                          minWidth="130px"
                        />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", width: "fit-content" }}>
                        <label className="projects-file-label">
                          {zipFile ? `${zipFile.name} (${(zipFile.size / 1024 / 1024).toFixed(1)} MB)` : "Choose .zip file…"}
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".zip"
                            style={{ display: "none" }}
                            onChange={onFileChange}
                          />
                          {zipFile && (
                            <span
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setZipFile(null);
                                setZipName("");
                                if (fileInputRef.current) fileInputRef.current.value = "";
                              }}
                              style={{ marginLeft: 8, cursor: "pointer", fontSize: 16, color: "var(--brand-muted)", lineHeight: 1 }}
                              title="Remove file"
                            >
                              ✕
                            </span>
                          )}
                        </label>
                      </Box>
                      <Typography variant="caption" sx={{ color: "var(--brand-muted)" }}>
                        Zip your source code directory and upload it. The archive is extracted and indexed exactly like a Git repo.
                      </Typography>
                    </Box>
                  </form>
                </Fade>
              )}

              {error && (
                <Fade in timeout={200}>
                  <Alert severity="error" sx={{ mt: 1.5, borderRadius: "var(--radius-sm)" }}>
                    {error}
                  </Alert>
                </Fade>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grow>

      {/* Repos table */}
      <Fade in timeout={600}>
        <Card className="orgs-create-card" sx={{ mt: 3 }}>
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            {loading ? (
              <Box sx={{ p: 3 }}>
                <LinearProgress sx={{ borderRadius: 2, "& .MuiLinearProgress-bar": { backgroundColor: "var(--brand-pink)" } }} />
                <Typography variant="body2" className="login-subtitle" sx={{ mt: 2, mb: 0 }}>
                  Loading repositories…
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small" sx={{ tableLayout: "fixed" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell className="projects-th" sx={{ width: "38%" }}>Source</TableCell>
                      <TableCell className="projects-th" sx={{ width: "16%" }}>Created at</TableCell>
                      <TableCell className="projects-th" sx={{ width: "12%" }}>Branch</TableCell>
                      <TableCell className="projects-th" sx={{ width: "12%" }}>Latest job</TableCell>
                      <TableCell className="projects-th" sx={{ width: "14%" }}>Commit / hash</TableCell>
                      <TableCell className="projects-th" sx={{ width: "8%" }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...repos].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).map((r, index) => {
                      const pid = r.project_id ?? getProjectId() ?? "";
                      const latest = (jobs[r.id] || [])[0];
                      const label = r.display_name || r.repo_url;
                      const isUpload = r.source_type === "upload";
                      return (
                        <TableRow key={r.id} className="projects-row stagger-item" style={{ animationDelay: `${index * 0.04}s` }}>
                          <TableCell className="projects-td">
                            {isUpload && (
                              <Chip label="zip" size="small" className="projects-zip-chip" />
                            )}
                            {label}
                          </TableCell>
                          <TableCell className="projects-td" sx={{ color: "grey !important", fontSize: "13px !important" }}>
                            {formatDate(r.created_at)}
                          </TableCell>
                          <TableCell className="projects-td">{isUpload ? "—" : r.ref}</TableCell>
                          <TableCell className="projects-td">
                            {latest ? (
                              <Chip
                                label={latest.state}
                                size="small"
                                className={`projects-status-chip ${latest.state}`}
                              />
                            ) : "—"}
                          </TableCell>
                          <TableCell className="projects-td" sx={{ fontFamily: "monospace", color: "var(--brand-muted)" }}>
                            {r.last_commit_sha?.slice(0, 8) || "—"}
                          </TableCell>
                          <TableCell className="projects-td projects-actions">
                            <Link to={`/projects/${pid}/repos/${r.id}/explore`} className="projects-explore-link">
                              <img src="/arrow_right.png" alt="Go" style={{ width: 18, height: 18 }} />
                            </Link>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {repos.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: "center", py: 6 }}>
                          <Box className="empty-state">
                            <Box className="empty-state-icon">◎</Box>
                            <Typography className="empty-state-title">No repos yet</Typography>
                            <Typography className="empty-state-desc">Index a repository above to get started with code analysis.</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Fade>
    </Box>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const day = d.getDate();
  const suffix = [11,12,13].includes(day) ? "th" : ({1:"st",2:"nd",3:"rd"} as Record<number,string>)[day % 10] || "th";
  const month = d.toLocaleString(undefined, { month: "long" });
  const year = d.getFullYear();
  let hours = d.getHours();
  const mins = d.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;
  return `${day}${suffix} ${month} ${year} (${hours}:${mins}${ampm})`;
}
